import React from 'react';
import { grey } from '@mui/material/colors';
import { FormSelect } from 'components/form';
import { Theme } from '@mui/material/styles';
import { Nullable, SelectOption } from 'global';
import makeStyles from '@mui/styles/makeStyles';
import { Board } from 'components/shared/Board';
import { CircularProgress, Stack, Box } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { BoardColumnEntity, MoveCardEventParams, SummaryProps } from 'components/shared/Board/types';

import { Card } from './Card';
import { Summary } from './Summary';
import { ColumnHeader } from './ColumnHeader';
import { DealPipelineListItem } from '../types';

interface Props {
  isStagesLoading: boolean;
  departments: SelectOption[];
  isDealInfoLoading: boolean;
  isDepartmentsLoading: boolean;
  summary: Nullable<SummaryProps>;
  columns: BoardColumnEntity<DealPipelineListItem>[];
  onDepartmentChange: (departmentId: string) => Promise<void>;
  onMoveCard: (params: MoveCardEventParams & { cardEntity: DealPipelineListItem }) => Promise<void>;
}

export const DealPipeLine = ({
  summary,
  columns,
  onMoveCard,
  departments,
  isStagesLoading,
  isDealInfoLoading,
  onDepartmentChange,
}: Props) => {
  const classes = useStyles();

  return (
    <Stack className={classes.cardContainer}>
      <Stack className={classes.stagesWrapper}>
        <RenderWithCondition
          condition={!!summary}
          fallBack={<Summary avgDealSize={0} totalActiveDeals={0} totalDealAmount={0} />}
        >
          <Summary {...summary!} />
        </RenderWithCondition>

        <FormSelect
          label="Department"
          options={departments}
          disabled={!departments?.length}
          onValueChange={onDepartmentChange}
          defaultValue={departments[0]?.value}
        />
      </Stack>

      <RenderWithFallback
        isLoading={isStagesLoading || isDealInfoLoading}
        fallBack={
          <Box className={classes.boardLoading}>
            <CircularProgress />
          </Box>
        }
      >
        <Board<DealPipelineListItem>
          columns={columns}
          onMoveCard={onMoveCard}
          renderCard={deal => <Card deal={deal} />}
          renderColumnHeader={column => <ColumnHeader column={column} />}
        />
      </RenderWithFallback>
    </Stack>
  );
};

const useStyles = makeStyles<Theme>(({ palette }) => ({
  cardContainer: {
    gap: '0.5rem',
    height: '100%',
    overflow: 'auto',
  },
  boardLoading: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.background.paper,
  },
  stagesWrapper: {
    gap: '1rem',
    backgroundColor: palette.background.paper,

    '& .stagesList': {
      gap: '1rem',
      flexDirection: 'row',

      '& .stageSkeleton': {
        minHeight: '4rem',
        minWidth: '10rem',
        borderRadius: '0.5rem',
        backgroundColor: grey[100],
      },

      '& .stage': {
        padding: '1rem',
        display: 'flex',
        minHeight: '4rem',
        minWidth: '10rem',
        alignItems: 'center',
        borderRadius: '0.5rem',
        justifyContent: 'center',
        backgroundColor: grey[100],
      },
    },
  },
}));
